/**
 * Global workspace-readonly bus.
 *
 * When any oRPC call returns 423 with `error: 'WORKSPACE_READ_ONLY'`,
 * the oRPC client fires a single event on this bus. The UserProvider
 * subscribes and reacts by re-running `whoami()` so the new tenant
 * status (paused / pending_deletion) lands in React state and the
 * status banner appears immediately — no page reload required.
 *
 * Mirrors the auth-failure bus pattern (`./auth-failure.ts`):
 *   - Module-level singleton, no React context, no circular imports
 *   - Debounced via `firing` so a flurry of concurrent 423s only
 *     triggers one whoami refresh
 *   - 2-second cooldown so a subsequent write that's still blocked
 *     can fire a fresh event (e.g. user dismisses the banner — wait,
 *     they can't — but they might click another action)
 *
 * The 423 itself is still surfaced as a normal oRPC error to the
 * caller; this bus is purely for side-effects. The banner reads
 * the latest status from useUser(), so the local mutation site
 * doesn't need to do anything special — its existing toast still
 * fires, and by the time the toast lands the banner is also visible.
 */

type Handler = () => void

const handlers = new Set<Handler>()
let firing = false

export function onWorkspaceReadOnly(handler: Handler): () => void {
  handlers.add(handler)
  return () => {
    handlers.delete(handler)
  }
}

export function fireWorkspaceReadOnly(): void {
  if (firing) return
  firing = true
  for (const handler of handlers) {
    try {
      handler()
    } catch (err) {
      console.warn('[workspace-readonly] handler threw:', err)
    }
  }
  // Cooldown so the next write attempt (after the banner has
  // landed and the user has presumably read it) can still trigger
  // a fresh refresh if the status changes again — e.g. an admin
  // resumes the workspace remotely while the user is staring at
  // the banner.
  setTimeout(() => {
    firing = false
  }, 2000)
}
